"use client"

import { useMutation, type UseMutationOptions } from "@tanstack/react-query"
import { toast } from "sonner"
type ApiMutationOptions<TData, TVariables> = Omit<
    UseMutationOptions<TData, Error, TVariables>,
    "mutationFn"
> & {
    showErrorToast?: boolean
    showSuccessToast?: boolean
    successMessage?: string
}

export function useApiMutation<TData, TVariables>(
    mutationFn: (variables: TVariables) => Promise<TData>,
    options?: ApiMutationOptions<TData, TVariables>
) {
    const {
        showErrorToast = true,
        showSuccessToast = false,
        successMessage = "Success",
        onError,
        onSuccess,
        ...mutationOptions
    } = options ?? {}

    return useMutation({
        mutationFn,
        onError: (error, variables, onMutateResult, context) => {
            if (showErrorToast) {
                const message = error instanceof Error ? error.message : "An error occurred"
                toast.error(message)
            }
            onError?.(error, variables, onMutateResult, context)
        },
        onSuccess: (data, variables, onMutateResult, context) => {
            if (showSuccessToast) {
                toast.success(successMessage)
            }
            onSuccess?.(data, variables, onMutateResult, context)
        },
        ...mutationOptions,
    })
}
